import { Table } from '@heroui/react'

import type { PosSession } from '../types'
import {
  branchLabel,
  chipStyle,
  relativeTime,
  sessionStatusStyles,
  varianceLabel,
} from './pos-format'
import { StatusChip } from './status-chip'

interface SessionsTableProps {
  sessions: PosSession[]
  /** Opens the drawer's detail — its Z document, and the force-close. */
  onOpen: (session: PosSession) => void
}

/**
 * One row per trading period, open or closed.
 *
 * The variance is the column this screen exists for, so it is the only one that
 * changes colour: a drawer that is out by more than a rounding cent is painted
 * amber, and an open session shows a dash rather than a zero — nobody has
 * counted it yet, and a zero would claim otherwise.
 */
export function SessionsTable({ sessions, onOpen }: SessionsTableProps) {
  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content
          aria-label="Cash sessions"
          selectionMode="single"
          selectionBehavior="replace"
          onRowAction={(key) => {
            const session = sessions.find((row) => row.id === String(key))

            if (session) onOpen(session)
          }}
        >
          <Table.Header>
            <Table.Column id="number" isRowHeader>
              Session
            </Table.Column>
            <Table.Column id="register">Register</Table.Column>
            <Table.Column id="branch">Branch</Table.Column>
            <Table.Column id="openedBy">Opened by</Table.Column>
            <Table.Column id="openedAt">Opened at</Table.Column>
            <Table.Column id="status">Status</Table.Column>
            <Table.Column id="variance">Variance</Table.Column>
          </Table.Header>

          <Table.Body>
            {sessions.map((session) => (
              <Table.Row key={session.id} id={session.id}>
                <Table.Cell>
                  <span className="font-medium">{session.number}</span>
                </Table.Cell>

                <Table.Cell>
                  <span className="tabular-nums">{session.registerCode ?? '—'}</span>
                </Table.Cell>

                <Table.Cell>
                  <span className="text-accent">{branchLabel(session.branch)}</span>
                </Table.Cell>

                <Table.Cell>{session.openedByName ?? <span className="text-muted">—</span>}</Table.Cell>

                <Table.Cell>
                  <span className="text-muted tabular-nums">
                    {relativeTime(session.openedAt)}
                  </span>
                </Table.Cell>

                <Table.Cell>
                  <StatusChip style={chipStyle(sessionStatusStyles, session.status)} />
                </Table.Cell>

                <Table.Cell>
                  <span
                    className={`tabular-nums ${
                      session.varianceNeedsAttention
                        ? 'font-medium text-amber-700 dark:text-amber-400'
                        : session.variance === null
                          ? 'text-muted'
                          : ''
                    }`}
                  >
                    {varianceLabel(session.variance)}
                  </span>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  )
}
